'use client'

import { useFetchLoadTypes } from '@repo/features/load-type'
import {
  Button,
  buttonVariants,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
} from '@repo/ui/components'
import { ChevronDown, Search } from '@repo/ui/icons'
import { cn, VariantProps } from '@repo/ui/lib/utils'
import { ComponentProps, ReactNode, Ref, useEffect, useMemo, useState } from 'react'

import { useFieldContext } from '../../lib/form-context.ts'

const DEFAULT_LOAD_TYPE_SLUG = 'curtain'

export const VehicleTypeInput = ({
  className,
  defaultSlug = DEFAULT_LOAD_TYPE_SLUG,
  description,
  disabled,
  isPending,
  label,
  placeholder = 'Select vehicle type...',
  triggerRef,
  variant = 'outline',
}: ComponentProps<'button'> & {
  className?: string
  defaultSlug?: null | string
  description?: ReactNode
  disabled?: boolean
  isPending?: boolean
  label?: ReactNode
  placeholder?: string
  triggerRef?: Ref<HTMLButtonElement>
  variant?: VariantProps<typeof buttonVariants>['variant']
}) => {
  const [open, setOpen] = useState(false)
  const field = useFieldContext<null | number | undefined>()

  const { data, isPending: isLoading } = useFetchLoadTypes()

  const loadTypes = useMemo(() => {
    return [...(data?.raw ?? [])].sort((a, b) => a.title.localeCompare(b.title))
  }, [data?.raw])

  const selectedLoadType = loadTypes.find(loadType => loadType.id === field.state.value)
  const displayTitle = selectedLoadType?.title ?? placeholder

  const defaultLoadTypeId = defaultSlug
    ? loadTypes.find(loadType => loadType.slug === defaultSlug)?.id
    : undefined

  useEffect(() => {
    if (defaultLoadTypeId !== undefined && field.state.value == null) {
      field.handleChange(defaultLoadTypeId)
    }
  }, [defaultLoadTypeId, field])

  const handleOnSelect = (value: number) => {
    field.handleChange(value)
    setOpen(false)
  }

  return (
    <Field className="gap-1" data-sentry-element="Field" data-sentry-component="VehicleTypeInput" data-sentry-source-file="load-type-input.tsx">
      {label && <FieldLabel>{label}</FieldLabel>}
      <FieldContent data-sentry-element="FieldContent" data-sentry-source-file="load-type-input.tsx">
        <Popover onOpenChange={setOpen} open={open} data-sentry-element="Popover" data-sentry-source-file="load-type-input.tsx">
          <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="load-type-input.tsx">
            <Button
              className={cn('h-8 justify-between gap-1.5 py-1 pr-1 pl-2.5', className)}
              disabled={disabled || isPending || isLoading}
              ref={triggerRef}
              type="button"
              variant={variant}
            data-sentry-element="Button" data-sentry-source-file="load-type-input.tsx">
              <span
                className={cn(
                  !selectedLoadType && 'text-muted-foreground',
                  'flex items-center gap-1.5 truncate',
                )}
              >
                <Search data-sentry-element="Search" data-sentry-source-file="load-type-input.tsx" />
                {displayTitle}
              </span>
              <ChevronDown data-sentry-element="ChevronDown" data-sentry-source-file="load-type-input.tsx" />
              {(isPending || isLoading) && <Spinner className="size-4 shrink-0" />}
            </Button>
          </PopoverTrigger>

          <PopoverContent align="start" className="max-w-100 p-0" collisionPadding={10} data-sentry-element="PopoverContent" data-sentry-source-file="load-type-input.tsx">
            <Command data-sentry-element="Command" data-sentry-source-file="load-type-input.tsx">
              <CommandInput placeholder="Search vehicle types..." data-sentry-element="CommandInput" data-sentry-source-file="load-type-input.tsx" />
              <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="load-type-input.tsx">No vehicle type found.</CommandEmpty>
              <CommandList className="max-h-96" data-sentry-element="CommandList" data-sentry-source-file="load-type-input.tsx">
                {loadTypes.map(loadType => (
                  <CommandItem
                    key={loadType.id}
                    onSelect={() => handleOnSelect(loadType.id)}
                    value={loadType.title}
                  >
                    {loadType.title}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </FieldContent>
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldError errors={field.state.meta.errors} data-sentry-element="FieldError" data-sentry-source-file="load-type-input.tsx" />
    </Field>
  )
}
